

import detailedChill from "@/assets/detailed-chill.png";
import detailedEnergy from "@/assets/detailed-energy.png";
import detailedFocus from "@/assets/detailed-focus.png";
import detailedSleep from "@/assets/detailed-sleep.png";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const products = [
  {
    state: "CHILL",
    tagline: "Relaxed. Not sedated.",
    description: "Calm without sedation. L-theanine meets lavender for grounded ease.",
    image: detailedChill,
    bg: "bg-[hsl(var(--pastel-blue))]",
    ingredients: ["L-Theanine", "Lavender Extract", "Chamomile", "Lemon Balm"],
  },
  {
    state: "ENERGY",
    tagline: "Lift, not spike.",
    description: "Clean, sustained energy. B-vitamins + green tea caffeine for a lasting boost.",
    image: detailedEnergy,
    bg: "bg-[hsl(var(--pastel-peach))]",
    ingredients: ["Green Tea Caffeine", "B-Vitamins", "Guarana", "Ginseng"],
  },
  {
    state: "FOCUS",
    tagline: "Sharp mind. Smooth energy.",
    description: "Clarity and flow without the jitters. Green tea + maca root for sustained presence.",
    image: detailedFocus,
    bg: "bg-[hsl(var(--pastel-yellow))]",
    ingredients: ["Maca Root", "Green Tea", "Lion's Mane", "Bacopa"],
  },
  {
    state: "SLEEP",
    tagline: "Power down naturally.",
    description: "Wind-down and recovery. Tart cherry + magnesium — rest that works.",
    image: detailedSleep,
    bg: "bg-[hsl(var(--pastel-lavender))]",
    ingredients: ["Tart Cherry", "Magnesium", "Passionflower", "Zinc"],
  },
];

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const MeetTheStates = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="meet-the-states" className="bg-background py-24 md:py-32">
      <div ref={ref} className="mx-auto max-w-6xl px-6">
        <div
          className={`mb-16 text-center transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-foreground/50">
            Four modes. One mission.
          </p>
          <h2 className="mt-3 heading-bold text-4xl text-foreground sm:text-5xl lg:text-[4rem] lg:leading-[0.95]">
            Meet the States
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4 lg:gap-6">
          {products.map((product, i) => (
            <div
              key={product.state}
              className={`transition-all duration-700 ease-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 120 + 200}ms` }}
            >
              <div className="group flex flex-col items-center">
                <div
                  className={`card-glow relative w-full aspect-[3/4] rounded-3xl overflow-hidden flex items-center justify-center p-3 sm:p-4 ${product.bg}`}
                >
                  {/* Watermark state name */}
                  <span className="watermark-text text-foreground">
                    {product.state}
                  </span>

                  <img
                    src={product.image}
                    alt={`${product.state} — ${product.tagline}`}
                    className="relative z-10 w-full h-full object-contain drop-shadow-xl scale-110 transition-transform duration-500 group-hover:scale-[1.15]"
                    loading="lazy"
                  />
                </div>

                <div className="mt-4 text-center px-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/50">
                    {product.state}
                  </p>
                  <p className="mt-1.5 font-bold text-lg sm:text-xl text-foreground leading-tight">
                    {product.tagline}
                  </p>
                  <p className="mt-2 text-xs sm:text-sm leading-relaxed text-foreground/60">
                    {product.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MeetTheStates;
